import * as db from '@shared/types/database'
import { QuestionType } from '@shared/types/database'
import { Question, Respondent, Response } from '@/store/modules/survey-store-module'
import { ResponseState } from '@alpha/shared/src/entities/ResponseEvent'
import { RespondentSurveyState } from '@alpha/shared/src/entities/RespondentSurveyEvent'
import store from '@/store'
import { NestedResponses } from '@/components/Survey/Utilities/ChartUtilities'
import { intersection, uniqBy } from 'lodash'

const QuestionTypeOpenEndMap: Partial<Record<db.QuestionType, keyof Pick<Response, 'explainAnswer' | 'textAnswer'>>> = {
  'single-select': 'explainAnswer',
  textarea: 'textAnswer',
  rate: 'explainAnswer'
}

const QuestionTypeToAnswerTypeMap: Record<db.QuestionType, keyof Pick<Response, 'textAnswer' | 'textArrayAnswer' | 'textMapAnswer'>> = {
  /** Optional explainAnswer */
  'single-select': 'textAnswer',
  rank: 'textArrayAnswer',
  'matrix-likert': 'textMapAnswer',
  'multi-select': 'textArrayAnswer',
  textarea: 'textAnswer',
  /** Optional explainAnswer */
  rate: 'textAnswer',
  number: 'textAnswer',
  variant: 'textAnswer',
  /** Not available in sample test */
  'rate-only': 'textAnswer',
  /** Not available in sample test */
  task: 'textAnswer',
  /** Not available in sample test */
  description: 'textAnswer',
  /** Not available in sample test */
  verbal: 'textAnswer'
}

export enum QuestionOptions {
  NA = 'Not Applicable'
}

export const getAnswerType = (type: db.QuestionType) => {
  return QuestionTypeToAnswerTypeMap[type]
}

export const generateSliceByQuestionBuckets = (question: Question) => {
  const respondentsByQuestionResponse: Record<string, string[]> = {}

  // Generate the buckets like this so we can ensure the ordering
  switch (question.type as QuestionType) {
    // Currently we only support slicing by these three question types
    case QuestionType.SingleSelect:
    case QuestionType.MultiSelect: {
      // Show the options in order, don't really care what the "winner" is here
      const sortedOptions = question.options!.sort((a, b) => (a.order > b.order) ? 1 : -1)

      sortedOptions.forEach((option) => {
        respondentsByQuestionResponse[option.text] = []
      })
      break
    }
    case QuestionType.Rate:
    case QuestionType.RateOnly: {
      const options = question.scaleProperties!
      for (let i = options.minRating!; i <= options.maxRating!; i++) {
        respondentsByQuestionResponse[i] = []
      }
      break
    }
    default: // TODO: What should we do here? Make assumptions?
  }

  return respondentsByQuestionResponse
}

export const generateResponseToggleMap = (responses: Response[]) => {
  const responseToggle = responses.reduce((acc: Record<string, boolean>, response) => {
    const visible = response.status === ResponseState.APPROVED_BY_DS ||
        response.status === ResponseState.APPROVED_BY_USER
    acc[response.id] = visible
    return acc
  }, {})
  return responseToggle
}

export const generateRespondentToggleMap = (respondents: Respondent[]) => {
  const removals = respondents.reduce((acc, respondent) => {
    const visible = respondent.status === RespondentSurveyState.APPROVED_BY_USER ||
        respondent.status === RespondentSurveyState.APPROVED_BY_DS
    acc[respondent.id] = visible
    return acc
  }, {} as Record<string, boolean>)
  return removals
}

export const getAnswerFromResponse = (response: Response, type: db.QuestionType) => {
  return response[getAnswerType(type)]
}

export const removeHiddenResponses = (responses: Response[]): Response[] => {
  return responses.filter(response => {
    const id = response.id

    const respondentId = response.respondentSurveyId!
    const { responseRemovalMap, respondentRemovalMap } = store.state.survey
    const isVisible = respondentRemovalMap[respondentId] && responseRemovalMap[id]
    return isVisible
  })
}

export const getQuestionOpenEnds = (question: Question & { responses: Response[] }) => {
  let responses
  if (question.hasOtherOpt && !question.hasExplainOpt) {
    responses = question.responses.filter(response => {
      return isOther(response)
    })
  } else {
    responses = question.responses
  }
  return responses
}

export const addAnswerToResponse = (response: Response, type: db.QuestionType): Response & { answer: any } => {
  return {
    ...response,
    answer: getAnswerFromResponse(response, type)
  }
}

export const isOpenEnd = (question: Question) => {
  return question.type === db.QuestionType.TextArea ||
  question.hasExplainOpt ||
  question.hasOtherOpt
}

export const getOpenEnd = (response: Response, question: Question) => {
  const attribute = QuestionTypeOpenEndMap[question.type as db.QuestionType]
  if (question.hasOtherOpt && !question.hasExplainOpt) return response.otherAnswer
  if (question.hasOtherOpt && question.hasExplainOpt && response.answer.includes('Other')) return `${response.otherAnswer} - ${response.explainAnswer}`
  if (attribute) return response[attribute]
}

/**
 * TODO: Description
 * @param question
 */
export const getOptionsTextFromQuestion = (question: Question) => {
  const categories = []

  if (!question.options) return []
  for (const option of question.options) {
    const order = option.shuffle ? undefined : option.order
    const { text, hash } = option
    categories.push({
      text,
      hash,
      order
    })
  }
  return categories
}

// TODO: Comment
export const getResponsesBySegment = (respondents: Record<string, string[]>, responses: Response[], type: QuestionType) => {
  const responsesBySeg: NestedResponses = {}
  const respondentIdToResponseMap = generateRespondentIdToResponseMap(responses)

  // Break out the bucket names / respondentIds ...
  for (const [bucketKey, respondentIds] of Object.entries(respondents)) {
    responsesBySeg[bucketKey] = []
    // Iterate over the respondentIds
    for (const respondentSurveyId of respondentIds) {
      // Find the respondent's response in given responses, put it in correct bucket using key from above
      const response = respondentIdToResponseMap[respondentSurveyId]
      if (response) {
        const responseWithAnswer = addAnswerToResponse(response, type)
        responsesBySeg[bucketKey].push(responseWithAnswer)
      }
    }
  }

  return responsesBySeg
}

// TODO: Comment
export const generateRespondentIdToResponseMap = (responses: Response[]) => {
  const respondentIdToResponseMap: Record<string, Response> = {}
  for (const response of responses) {
    respondentIdToResponseMap[response.respondentSurveyId!] = response
  }
  return respondentIdToResponseMap
}

/***
 * Check if the option is shuffled. Shuffled means that the options are shuffled when shown to respondents
 * The alternative is that they are locked.
 * @param optionName The name of the option being checked
 * @param options The list of options to find it in
 */
export const isOptionShuffled = (optionName: string, options: { text: string, shuffle: boolean }[]) => {
  if (!options) return false
  return options.find(option => option.text === optionName)?.shuffle
}

/***
 * Get the order of the given option. Order starts at 0 and represents the order it is shown in list of choices
 * for a question in a survey
 * @param optionName The name of the option to get the order for
 * @param options The list of options to find it in
 */
export const getOptionOrder = (optionName: string, options: { text: string, order: number }[]) => {
  return options.find(option => option.text === optionName)!.order
}

export const parentQuestions = (question: Question) => {
  const parentQuestions:Question[] = []
  if (question.parentQuestionIds && question.parentQuestionIds.length) {
    question.parentQuestionIds.forEach(id => {
      const q = store.getters.survey.questionById(id!)
      if (q) parentQuestions.push(q)
    })
  }
  return parentQuestions
}

export const respondentCountText = (question: Question, parentQuestions: Question[], filteredIndexResponses: Response[], indexResponses: Response[]):string => {
  // Wordclouds and sentiment show filtered counts
  if (question.type === db.QuestionType.TextArea) {
    const noResponsesHidden = filteredIndexResponses.length === indexResponses.length
    return noResponsesHidden ? `${indexResponses.length} Respondents`
      : `${filteredIndexResponses.length} of ${indexResponses.length} Respondents`
  }

  const hasVariant = !!parentQuestions.filter(q => q.type === QuestionType.Variant).length

  // If tagged question, show parent question context so user knows why responses are lower
  // Gets the count by getting all parent responses and filtering them by distinct respondent id and tag
  if (parentQuestions?.length && !hasVariant) {
    const qualifyingTags = question.qualifyingTags as string[]
    let parentResponsesByQualifyingTags:Response[] = []
    const tagToParentCount:Record<string, Response[]> = {}
    parentQuestions.forEach(parentQuestion => {
      const parentQuestionResponses = store.getters.survey.responsesByQuestion(parentQuestion.id)

      parentResponsesByQualifyingTags = parentResponsesByQualifyingTags.concat(getParentResponsesByQualifyingTags(parentQuestion, qualifyingTags, parentQuestionResponses))
      if (parentQuestion.options) {
        parentQuestion.options.forEach(o => {
          if (o.qualificationTags) {
            o.qualificationTags.forEach(t => {
              if (t) {
                tagToParentCount[t] = tagToParentCount[t] ? tagToParentCount[t].concat(parentQuestionResponses) : parentQuestionResponses
              }
            })
          }
        })
      }
    })
    let totalParentResponses:Response[] = []
    question.qualifyingTags.forEach(t => {
      if (t) {
        totalParentResponses = totalParentResponses.concat(tagToParentCount[t] || [])
      }
    })
    const qualifiedResponseCount = uniqBy(parentResponsesByQualifyingTags, r => r.respondentSurveyId).length
    const totalParentResponseCount = uniqBy(totalParentResponses, r => r.respondentSurveyId).length
    if (qualifiedResponseCount > totalParentResponseCount) {
      console.error('Error in parent logic N sizes', {
        questionId: question.id,
        tagToParentCount,
        qualifiedResponseCount,
        totalParentResponseCount,
        qualifyingTags,
        parentQuestions
      })
    }

    return `${qualifiedResponseCount} out of ${totalParentResponseCount} Respondents`
  } else {
    return `${indexResponses.length} Respondents`
  }
}

export const getParentResponsesByQualifyingTags = (parentQuestion: Question, questionQualifyingTags:string[], parentResponses: Response[]) => {
  if (parentQuestion.type !== QuestionType.Variant && parentQuestion.options?.length) {
    const options:string[] = parentQuestion.options.reduce((optionsList:string[], o) => {
      const simTags = intersection(o.qualificationTags, questionQualifyingTags)
      return simTags.length ? optionsList.concat(o.text) : optionsList
    }, [])

    const responseByQualifyingTags = parentResponses.filter(r => {
      return intersection(r.textArrayAnswer || [r.textAnswer || ''], options).length
    })

    return responseByQualifyingTags
  } else {
    return []
  }
}

export const isOther = (response: Response): boolean => {
  switch (typeof response.answer) {
    case 'string':
      return response.answer === 'Other'
    case 'object':
      return Array.isArray(response.answer) ? response.answer.includes('Other') : false
    default:
      return false
  }
}
